import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import * as EvidenceSelectors from './data-access.selectors';
import * as EvidenceActions from './data-access.actions';
import { IEvidenceState } from './data-access.state';
import { EvidenceAndPreview, Location } from '../models/evidence.models';
import { SetLocationRequest, UpdateEvidenceRequest } from '../models/evidence-request.models';
import { ActionType, EvidenceDetailsActionResult } from '../models/evidence-action-result.models';
import { EvidenceLink, EvidenceLinkType, OpenAiTaskStatus } from '@shared/utils/evidence-management';
import { EvidenceDetailsResponse } from '../models/evidence-response.models';

@Injectable({
  providedIn: 'root',
})
export class EvidenceFacade {
  public constructor(private readonly store: Store<IEvidenceState>) {}

  public fetchEvidenceByUid(uid: string): void {
    this.store.dispatch(EvidenceActions.getEvidence({ uid: uid }));
  }

  public getEvidence(): Observable<EvidenceAndPreview> {
    return this.store.select(EvidenceSelectors.evidenceSelector).pipe(filter((x): x is EvidenceAndPreview => !!x));
  }

  public getEvidenceEmitDistinctUidValue(): Observable<EvidenceAndPreview> {
    return this.store.select(EvidenceSelectors.evidenceSelector).pipe(
      filter((x): x is EvidenceAndPreview => !!x),
      distinctUntilChanged((previous, current) => previous.evidence.uid === current.evidence.uid)
    );
  }

  public getEvidenceLocation(): Observable<Location> {
    return this.store.select(EvidenceSelectors.evidenceLocationSelector).pipe(filter((x): x is Location => !!x));
  }

  public isLoading(): Observable<boolean> {
    return this.store.select(EvidenceSelectors.isLoading);
  }

  public isLocationLoading(): Observable<boolean> {
    return this.store.select(EvidenceSelectors.evidenceIsLocationLoadingSelector);
  }

  public toggleEvidenceLockState(): void {
    this.store.dispatch(EvidenceActions.toggleLockEvidence());
  }

  public setEvidenceUid(evidenceUid: string): void {
    this.store.dispatch(EvidenceActions.setEvidenceUid({ uid: evidenceUid }));
  }

  public getEvidenceUid(): Observable<string> {
    return this.store.select(EvidenceSelectors.evidenceUidSelector).pipe(filter((x): x is string => x !== null));
  }

  public updateEvidence(updatedEvidenceDetails: UpdateEvidenceRequest): void {
    this.store.dispatch(EvidenceActions.updateEvidence({ updatedEvidenceDetails: updatedEvidenceDetails }));
  }

  public evidenceDetailsActionResult(actionType: ActionType): Observable<EvidenceDetailsActionResult> {
    return this.isLoading().pipe(
      switchMap((isLoading) => {
        if (isLoading) {
          return of();
        }

        return this.store.select(EvidenceSelectors.actionResult).pipe(filter((x): x is EvidenceDetailsActionResult => !!x && x.actionType === actionType));
      })
    );
  }

  public clearState(): void {
    this.store.dispatch(EvidenceActions.clearState());
  }

  public updateEvidenceLocation(location: SetLocationRequest): void {
    this.store.dispatch(EvidenceActions.updateEvidenceLocation({ location: location }));
  }

  public getEvidenceLinks(): Observable<EvidenceLink[]> {
    return this.store.select(EvidenceSelectors.evidenceLinks).pipe(filter((x): x is EvidenceLink[] => !!x));
  }

  public getEvidenceTranscriptionLink(): Observable<EvidenceLink | undefined> {
    return this.getEvidenceLinks().pipe(map((links) => links.find((x) => x.type === EvidenceLinkType.Transcription)));
  }

  public setUpEvidenceDetails(evidenceDetails: EvidenceDetailsResponse): void {
    this.store.dispatch(EvidenceActions.setUpEvidenceDetails({ evidenceDetails: evidenceDetails }));
  }

  public isLockCodeRequired(): Observable<boolean> {
    return this.store.select(EvidenceSelectors.isLockCodeNeeded);
  }

  public revertEvidence(isLocked: boolean): void {
    this.store.dispatch(EvidenceActions.revertEvidence({ isLocked }));
  }

  public setEvidenceAiSummaryStatus(status: OpenAiTaskStatus): void {
    this.store.dispatch(EvidenceActions.setAiSummaryStatus({ aiSummaryStatus: status }));
  }

  public requestTranscription(uid: string): void {
    this.store.dispatch(EvidenceActions.requestTranscription({ uid: uid }));
  }
}
